import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, Mail, MapPin, ArrowUpRight, MessageCircle } from "lucide-react";
import { site, nav, apartments, ui } from "@/data/site";
import BookButton from "@/components/BookButton";
import LocalConditions from "@/components/LocalConditions";

export default function Footer() {
  const year = new Date().getFullYear();
  const { pathname } = useLocation();
  return (
    <footer className="bg-white text-ink" data-testid="site-footer">
      {/* LARGE CTA */}
      <div className="container-x pt-24 md:pt-32">
        <div className="flex flex-col items-start justify-between gap-10 pb-4 lg:flex-row lg:items-end">
          <div className="max-w-3xl">
            <p className="eyebrow text-golden mb-6">{ui.ctaEyebrow}</p>
            <h2 className="display-1 text-ink">
              {ui.ctaA}
              <br />
              {ui.ctaB}<span className="italic text-golden">{ui.ctaAegean}</span>{ui.ctaC}
            </h2>
          </div>
          <div className="shrink-0">
            <BookButton testid="footer-book-btn" />
          </div>
        </div>
        <LocalConditions />
      </div>

      {/* LINK COLUMNS */}
      <div className="container-x py-16 md:py-20">
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-[1.6fr_1fr_1fr_1.2fr]">
          <div>
            <img src={site.logoColor} alt="Efi's Apartments" className="h-12 w-auto" />
            <p className="body-lg mt-6 max-w-sm !text-ink/70">
              {ui.footerDesc}
            </p>
          </div>

          <div>
            <p className="eyebrow text-golden mb-5">{ui.explore}</p>
            <ul className="space-y-3">
              {nav.map((item) => (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    aria-current={pathname === item.to ? "page" : undefined}
                    className={`transition-colors hover:text-golden ${pathname === item.to ? "text-golden" : "text-ink/70"}`}
                    data-testid={`footer-link-${item.to === "/" ? "home" : item.to.replace(/^\//, "").replace(/\//g, "-")}`}
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="eyebrow text-golden mb-5">{ui.accommodation}</p>
            <ul className="space-y-3">
              {apartments.map((apt) => {
                const aptActive = pathname === `/apartments/${apt.slug}`;
                return (
                  <li key={apt.slug}>
                    <Link
                      to={`/apartments/${apt.slug}`}
                      aria-current={aptActive ? "page" : undefined}
                      className={`group inline-flex items-start gap-1.5 transition-colors hover:text-golden ${aptActive ? "text-golden" : "text-ink/70"}`}
                      data-testid={`footer-apt-${apt.slug}`}
                    >
                      {apt.name}
                      <ArrowUpRight className={`mt-0.5 h-4 w-4 shrink-0 transition-opacity ${aptActive ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`} />
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>

          <div>
            <p className="eyebrow text-golden mb-5">{ui.getInTouch}</p>
            <ul className="space-y-4">
              <li>
                <a href={site.phoneHref} className="flex items-start gap-3 text-ink/70 transition-colors hover:text-golden">
                  <Phone className="mt-1 h-4 w-4 shrink-0" /> {site.phone}
                </a>
              </li>
              <li>
                <a href={site.whatsappHref} target="_blank" rel="noreferrer" className="flex items-start gap-3 text-ink/70 transition-colors hover:text-golden" data-testid="footer-whatsapp">
                  <MessageCircle className="mt-1 h-4 w-4 shrink-0" /> {site.whatsapp}
                </a>
              </li>
              <li>
                <a href={site.emailHref} className="flex items-start gap-3 text-ink/70 transition-colors hover:text-golden">
                  <Mail className="mt-1 h-4 w-4 shrink-0" /> {site.email}
                </a>
              </li>
              <li>
                <a href={site.mapsLink} target="_blank" rel="noreferrer" className="flex items-start gap-3 text-ink/70 transition-colors hover:text-golden">
                  <MapPin className="mt-1 h-4 w-4 shrink-0" /> {site.address}
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* OVERSIZED WORDMARK */}
      <div className="overflow-hidden px-[10px]">
        <p className="select-none whitespace-nowrap text-center font-serif leading-none text-ink/[0.06] text-[18vw]">
          Efi&rsquo;s Apartments
        </p>
      </div>

      {/* CREDENTIALS — MHTE / management */}
      <div
        className="container-x flex flex-col items-center justify-center gap-3 border-t border-ink/10 py-5 text-sm text-ink/55 sm:flex-row sm:gap-10"
        data-testid="footer-credentials"
      >
        <span className="inline-flex items-center gap-2" data-testid="footer-mhte-number">
          <img src={site.mhte.numberIcon} alt="MHTE — Greek Ministry of Tourism licence" className="h-6 w-6 shrink-0 object-contain" />
          <span className="tracking-wide">{site.mhte.number}</span>
        </span>
        <span className="inline-flex items-center gap-2" data-testid="footer-mhte-managed">
          <span className="tracking-wide">
            {site.mhte.managedPrefix}{" "}
            <a
              href={site.mhte.managedUrl}
              target="_blank"
              rel="noreferrer"
              className="font-medium underline-offset-2 transition-colors hover:text-golden hover:underline"
              data-testid="footer-hotel-operation-link"
            >
              {site.mhte.managedName}
            </a>
          </span>
        </span>
      </div>

      {/* BOTTOM BAR */}
      <div className="container-x grid gap-3 border-t border-ink/10 py-6 text-sm text-ink/50 md:grid-cols-3 md:items-center">
        <p className="text-center md:text-left">© Efi&rsquo;s Apartments — {year}. {ui.allRights}</p>
        <div className="text-center">
          <a
            href={site.credit.url}
            target="_blank"
            rel="noreferrer"
            className="transition-colors hover:text-golden"
            data-testid="footer-credit"
          >
            Website by <span className="font-medium">{site.credit.label}</span>
          </a>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 md:justify-end">
          <Link to="/policy" className="transition-colors hover:text-golden">{ui.privacyPolicy}</Link>
          <Link to="/booking-policy" className="transition-colors hover:text-golden">{ui.bookingPolicy}</Link>
        </div>
      </div>
    </footer>
  );
}
